

import React, { useState, useEffect } from 'react';

const API_URL = process.env.REACT_APP_API_URL;
const token = localStorage.getItem('accessToken');

export default function OtherTransactionsSection({ caseId, sofaResponse }) {
  const [transactions, setTransactions] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [newTx, setNewTx] = useState({
    person: '',
    street1: '',
    city: '',
    state: '',
    zip_code: '',
    relationship: '',
    description: '',
    payment_received: '',
    transfer_date: ''
  });
  const [editTx, setEditTx] = useState(null);

  // Fetch list
  useEffect(() => {
    if (!sofaResponse) return;
    (async () => {
      try {
        const res = await fetch(
          `${API_URL}/cases/${caseId}/sofa-other-transactions/?sofa_response=${sofaResponse.id}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!res.ok) throw new Error('Failed to fetch transactions');
        setTransactions(await res.json());
      } catch (err) {
        console.error(err);
      }
    })();
  }, [caseId, sofaResponse]);

  // Add modal open
  function openAddModal() {
    setNewTx({
      person: '',
      street1: '',
      city: '',
      state: '',
      zip_code: '',
      relationship: '',
      description: '',
      payment_received: '',
      transfer_date: ''
    });
    setModalOpen(true);
  }

  // Edit modal open
  function openEditModal(item) {
    setEditTx({ ...item });
    setEditModalOpen(true);
  }

  // Save new
  async function handleSaveNew() {
    try {
      const res = await fetch(
        `${API_URL}/cases/${caseId}/sofa-other-transactions/`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify(newTx)
        }
      );
      if (!res.ok) throw new Error();
      const created = await res.json();
      setTransactions(prev => [...prev, created]);
      setModalOpen(false);
    } catch (err) {
      console.error('Failed to add transaction', err);
    }
  }

  // Save edit
  async function handleSaveEdit() {
    try {
      const res = await fetch(
        `${API_URL}/cases/${caseId}/sofa-other-transactions/${editTx.id}/`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify(editTx)
        }
      );
      if (!res.ok) throw new Error();
      const updated = await res.json();
      setTransactions(prev =>
        prev.map(t => (t.id === updated.id ? updated : t))
      );
      setEditModalOpen(false);
    } catch (err) {
      console.error('Failed to update transaction', err);
    }
  }

  // Delete
  async function handleDelete(id) {
    try {
      const res = await fetch(
        `${API_URL}/cases/${caseId}/sofa-other-transactions/${id}/`,
        {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      if (!res.ok) throw new Error();
      setTransactions(prev => prev.filter(t => t.id !== id));
    } catch (err) {
      console.error('Failed to delete transaction', err);
    }
  }

  return (
    <section className="p-6 bg-white shadow-lg rounded mb-6">
      <h2 className="text-xl font-semibold mb-4">
        Question 18: Other Transactions Not Usual in Course of Business
      </h2>
      <p className="text-sm text-gray-600 mb-4">
            List transfers, sales, or trades not in the ordinary course of business within the last 2 years. Include outright transfres or transfers made as a security interest or mortgage.
        </p>
      {transactions.length === 0 ? (
        <p>No transactions found.</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 mb-4">
          {transactions.map(item => (
            <div
              key={item.id}
              className="relative p-4 bg-white shadow rounded cursor-pointer hover:shadow-md transition"
              onClick={() => openEditModal(item)}
            >
              <button
                onClick={e => {
                  e.stopPropagation();
                  handleDelete(item.id);
                }}
                className="absolute top-2 right-2 text-red-600 hover:text-red-800"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" clipRule="evenodd" d="M6 2a1 1 0 00-1 1v1H3a1 1 0 000
                    2h14a1 1 0 000-2h-2V3a1 1 0 00-1-1H6zM4
                    6v10a2 2 0 002 2h8a2 2 0 002-2V6H4zm3
                    2a1 1 0 000 2v6a1 1 0 102 0V8a1 1 0 00-2
                    0zm4 0a1 1 0 000 2v6a1 1 0 102 0V8a1 1 0
                    00-2 0z"/>
                </svg>
              </button>
              <h3 className="font-medium text-lg">{item.person}</h3>
              <p className="mt-1 text-sm">
                {item.street1}, {item.city}, {item.state} {item.zip_code}
              </p>
              {item.relationship && (
                <p className="mt-1 text-sm"><strong>Relationship:</strong> {item.relationship}</p>
              )}
              <p className="mt-2 text-sm"><strong>Description:</strong> {item.description}</p>
              <p className="mt-2 text-sm"><strong>Received:</strong> ${item.payment_received}</p>
              <p className="mt-1 text-sm"><strong>Date:</strong> {item.transfer_date}</p>
            </div>
          ))}
        </div>
      )}

      <button
        onClick={openAddModal}
        className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700"
      >
        Add Transaction
      </button>

      {/* Add Modal */}
      {modalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded shadow-lg w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">Add Transaction</h3>
            <div className="mb-3">
              <label className="block text-sm font-medium mb-1">Name</label>
              <input
                type="text"
                className="w-full border rounded px-2 py-1"
                value={newTx.person}
                onChange={e => setNewTx(prev => ({ ...prev, person: e.target.value }))}
              />
            </div>
            <div className="mb-3">
              <label className="block text-sm font-medium mb-1">Street 1</label>
              <input
                type="text"
                className="w-full border rounded px-2 py-1"
                value={newTx.street1}
                onChange={e => setNewTx(prev => ({ ...prev, street1: e.target.value }))}
              />
            </div>
            <div className="grid grid-cols-7 gap-4 mb-3">
              <div className="col-span-4">
                <label className="block text-sm font-medium mb-1">City</label>
                <input
                  type="text"
                  className="w-full border rounded px-2 py-1"
                  value={newTx.city}
                  onChange={e => setNewTx(prev => ({ ...prev, city: e.target.value }))}
                />
              </div>
              <div className="col-span-1">
                <label className="block text-sm font-medium mb-1">State</label>
                <input
                  type="text"
                  className="w-full border rounded px-2 py-1"
                  value={newTx.state}
                  onChange={e => setNewTx(prev => ({ ...prev, state: e.target.value }))}
                />
              </div>
              <div className="col-span-2">
                <label className="block text-sm font-medium mb-1">Zip Code</label>
                <input
                  type="text"
                  className="w-full border rounded px-2 py-1"
                  value={newTx.zip_code}
                  onChange={e => setNewTx(prev => ({ ...prev, zip_code: e.target.value }))}
                />
              </div>
            </div>
            <div className="mb-3">
              <label className="block text-sm font-medium mb-1">Relation to debtor</label>
              <input
                type="text"
                className="w-full border rounded px-2 py-1"
                value={newTx.relationship}
                onChange={e => setNewTx(prev => ({ ...prev, relationship: e.target.value }))}
              />
            </div>
            <div className="mb-3">
              <label className="block text-sm font-medium mb-1">Description</label>
              <textarea
                className="w-full border rounded px-2 py-1"
                value={newTx.description}
                onChange={e => setNewTx(prev => ({ ...prev, description: e.target.value }))}
              />
            </div>
            <div className="grid grid-cols-2 gap-4 mb-3">
            <div className="mb-3">
              <label className="block text-sm font-medium mb-1">Payment Received</label>
              <input
                type="number"
                className="w-full border rounded px-2 py-1"
                value={newTx.payment_received}
                onChange={e => setNewTx(prev => ({ ...prev, payment_received: e.target.value }))}
              />
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Transfer Date</label>
              <input
                type="date"
                className="w-full border rounded px-2 py-1"
                value={newTx.transfer_date}
                onChange={e => setNewTx(prev => ({ ...prev, transfer_date: e.target.value }))}
              />
            </div>
            </div>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setModalOpen(false)}
                className="px-3 py-1 rounded border"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveNew}
                className="px-3 py-1 rounded bg-indigo-600 text-white"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editModalOpen && editTx && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded shadow-lg w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">Edit Transaction</h3>
            <div className="mb-3">
              <label className="block text-sm font-medium mb-1">Name</label>
              <input
                type="text"
                className="w-full border rounded px-2 py-1"
                value={editTx.person}
                onChange={e => setEditTx(prev => ({ ...prev, person: e.target.value }))}
              />
            </div>
            <div className="mb-3">
              <label className="block text-sm font-medium mb-1">Street 1</label>
              <input
                type="text"
                className="w-full border rounded px-2 py-1"
                value={editTx.street1}
                onChange={e => setEditTx(prev => ({ ...prev, street1: e.target.value }))}
              />
            </div>
            <div className="grid grid-cols-7 gap-4 mb-3">
              <div className="col-span-4">
                <label className="block text-sm font-medium mb-1">City</label>
                <input
                  type="text"
                  className="w-full border rounded px-2 py-1"
                  value={editTx.city}
                  onChange={e => setEditTx(prev => ({ ...prev, city: e.target.value }))}
                />
              </div>
              <div className="col-span-1">
                <label className="block text-sm font-medium mb-1">State</label>
                <input
                  type="text"
                  className="w-full border rounded px-2 py-1"
                  value={editTx.state}
                  onChange={e => setEditTx(prev => ({ ...prev, state: e.target.value }))}
                />
              </div>
              <div className="col-span-2">
                <label className="block text-sm font-medium mb-1">Zip Code</label>
                <input
                  type="text"
                  className="w-full border rounded px-2 py-1"
                  value={editTx.zip_code}
                  onChange={e => setEditTx(prev => ({ ...prev, zip_code: e.target.value }))}
                />
              </div>
            </div>
            <div className="mb-3">
              <label className="block text-sm font-medium mb-1">Relationship</label>
              <input
                type="text"
                className="w-full border rounded px-2 py-1"
                value={editTx.relationship}
                onChange={e => setEditTx(prev => ({ ...prev, relationship: e.target.value }))}
              />
            </div>
            <div className="mb-3">
              <label className="block text-sm font-medium mb-1">Description</label>
              <textarea
                className="w-full border rounded px-2 py-1"
                value={editTx.description}
                onChange={e => setEditTx(prev => ({ ...prev, description: e.target.value }))}
              />
            </div>
            <div className="mb-3">
              <label className="block text-sm font-medium mb-1">Payment Received</label>
              <input
                type="number"
                className="w-full border rounded px-2 py-1"
                value={editTx.payment_received}
                onChange={e => setEditTx(prev => ({ ...prev, payment_received: e.target.value }))}
              />
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Transfer Date</label>
              <input
                type="date"
                className="w-full border rounded px-2 py-1"
                value={editTx.transfer_date}
                onChange={e => setEditTx(prev => ({ ...prev, transfer_date: e.target.value }))}
              />
            </div>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setEditModalOpen(false)}
                className="px-3 py-1 rounded border"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveEdit}
                className="px-3 py-1 rounded bg-indigo-600 text-white"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}